/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useCallback } from 'preact/hooks';
import { ToggleDetail } from '../UNSAFE_Collection';
import { useUser } from '../hooks/UNSAFE_useUser';
import { containsKey, Keys } from '../utils/UNSAFE_keys';

/**
 * A hook that handles mouse and keyboard gesture that toggles the expanded state.
 * @param keyExtractor
 * @param expanded
 * @param onToggle
 * @returns
 */
export function useExpandCollapse<K>(
  keyExtractor: (element: HTMLElement) => K | null,
  expanded: Keys<K>,
  onToggle?: (detail: ToggleDetail<K>) => void
) {
  const { direction } = useUser();

  const onClick = useCallback(
    (event: MouseEvent) => {
      handleToggle(event, keyExtractor, onToggle);
    },
    [keyExtractor, onToggle]
  );

  const onKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        handleToggle(event, keyExtractor, onToggle);
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        const isRtl = direction === 'rtl';
        const isLeftArrow = event.key === 'ArrowLeft' && !isRtl;
        handleArrowKey(isLeftArrow, event.target as HTMLElement, expanded, keyExtractor, onToggle);
        // arrow key might cause it to scroll horizontally
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [keyExtractor, onToggle, expanded, direction]
  );

  return onToggle ? { onClick, onKeyDown, onKeyUp } : {};
}

// prevent default (propagation) for keyDown to prevent scrolling the container
const onKeyDown = (event: KeyboardEvent) => {
  if (
    event.key === 'Enter' ||
    event.key === ' ' ||
    event.key === 'ArrowLeft' ||
    event.key === 'ArrowRight'
  ) {
    event.preventDefault();
  }
};

const handleArrowKey = <K>(
  isLeftArrow: boolean,
  target: HTMLElement,
  expanded: Keys<K>,
  keyExtractor: (element: HTMLElement) => K | null,
  onToggle?: (detail: ToggleDetail<K>) => void
) => {
  const itemKey = keyExtractor(target);
  if (onToggle && itemKey) {
    const contains = containsKey(expanded, itemKey);
    if ((isLeftArrow && contains) || (!isLeftArrow && !contains)) {
      onToggle({ value: itemKey });
    }
  }
};

const handleToggle = <K>(
  event: Event,
  keyExtractor: (element: HTMLElement) => K | null,
  onToggle?: (detail: ToggleDetail<K>) => void
) => {
  const itemKey = keyExtractor(event.target as HTMLElement);
  if (onToggle && itemKey) {
    onToggle({ value: itemKey });
    if (event.type === 'click') {
      event.stopPropagation();
    }
  }
};
